import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, Link } from "react-router-dom";
import { FaPen } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { fetchUsers } from "../../Redux/Reducers/UserManagementReducer";

function BasicInformation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const token = localStorage.getItem("vedhvidhi-admin-token");

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    setValue,
  } = useForm();

  const [profilePicture, setProfilePicture] = useState(null);  // Initial state is null
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/user/${id}`,
            {
              headers: {
                Authorization: token,
              },
            }
          );
          const userData = response?.data?.data;

          if (userData) {
            setValue("full_name", userData.fullName);
            setValue("email", userData.email);
            setValue("mobile_number", userData.mobileNumber);
            setValue("state", userData.state);
            setValue("city", userData.city);
            setValue("pincode", userData.pincode);
            setValue("landmark", userData.landmark);
            setProfilePicture(userData.profileImg || null); // Set the image URL or null
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          toast.error("Failed to fetch user data!");
        }
      }
    };

    fetchUserData();
  }, [id, setValue]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/imageUpload`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: token,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }

        const responseData = await response.json();
        const imageUrl = responseData?.data?.img;  // The image URL returned by the backend

        if (imageUrl) {
          setProfilePicture(imageUrl);  // Set the image URL to the state
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image.");
      }
    }
  };

  const onSubmit = async (data) => {
    let profileImgUrl = profilePicture || "";  // If no image uploaded, use empty string

    const payload = {
      fullName: data.full_name,
      email: data.email.toLowerCase(),
      profileImg: profileImgUrl,  // Use the updated profileImg URL
      mobileNumber: data.mobile_number,
      street: data.street,
      landmark: data.landmark || "",
      houseNo: data.house_no,
      pincode: data.pincode,
      state: data.state,
      city: data.city,
      dob: data.dob,
    };

    try {
      setLoading(true);

      if (id) {
        await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/user/${id}`, payload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });
        toast.success("User updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_BASEURL}/admin/user`, payload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });
        toast.success("User added successfully!");
      }

      navigate("/users-list");
    } catch (error) {
      setLoading(false);

      if (error?.response?.data?.errors) {
        error.response.data.errors.forEach((err) => {
          setError(err.field, {
            type: "manual",
            message: err.message,
          });
        });
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong!");
      }
    }
  };

  return (
    <>

      <button
        onClick={() => window.history.back()}
        style={{
          marginBottom: "10px",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          fontFamily: "monospace",
          fontSize: "18px",
          color: "#007bff",
          display: "flex",
          alignItems: "center",
          position: "relative",
          padding: "10px 20px",
        }}
      >
        <span style={{ marginRight: "8px" }}>&lt;</span>
        <span style={{ position: "relative", display: "inline-block" }}>
          Back
          <span
            style={{
              content: "''",
              position: "absolute",
              left: 0,
              bottom: -2,
              width: "100%",
              height: "1px",
              borderBottom: "2px solid #007bff",
            }}
          ></span>
        </span>
      </button>

      <div className="row align-items-center">
        <div className="border-0 mb-4">
          <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
            <h3 className="fw-bold mb-0">{id ? "Edit User" : "Add User"}</h3>
            <button
              type="submit"
              className="btn btn-primary text-uppercase px-3"
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="col-sm-12">
            <div className="file-upload">
              <label htmlFor="file-input">
                <img
                  src={profilePicture || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}  // Default image will be shown only if no profile picture is set
                  alt="Profile Icon"
                  className="icon"
                />
                <div className="edit-icon">
                  <FaPen />
                </div>
              </label>
              <input type="file" id="file-input" onChange={handleFileChange} />
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label">Full Name <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                placeholder="Full name"
                {...register("full_name", { required: "This field is required" })}
              />
              {errors.full_name && <span className="text-danger">{errors.full_name.message}</span>}
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label">Email <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="email"
                placeholder="Enter your email"
                {...register("email", {
                  required: "This field is required",
                  pattern: { value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/, message: "Invalid Email Address" },
                })}
              />
              {errors.email && <span className="text-danger">{errors.email.message}</span>}
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label">Mobile Number <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                maxLength={10}
                placeholder="Mobile Number"
                {...register("mobile_number", {
                  required: "This field is required",
                  pattern: { value: /^[0-9]{10}$/, message: "Mobile number must be 10 digits" },
                })}
              />
              {errors.mobile_number && <span className="text-danger">{errors.mobile_number.message}</span>}
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label">Pincode <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                maxLength={6}
                placeholder="Pincode"
                {...register("pincode", {
                  required: "This field is required",
                  pattern: { value: /^[0-9]{6}$/, message: "Pincode must be 6 digits" },
                })}
              />
              {errors.pincode && <span className="text-danger">{errors.pincode.message}</span>}
            </div>
          </div>

          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label">Landmark <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                placeholder="Landmark"
                {...register("landmark", { required: "This field is required" })}
              />
              {errors.landmark && <span className="text-danger">{errors.landmark.message}</span>}
            </div>
          </div>

          <div className="col-12 mt-4">
            {loading ? (
              <button type="submit" disabled className="btn btn-primary text-uppercase px-5">
                Loading...
              </button>
            ) : (
              <button type="submit" className="btn btn-primary text-uppercase px-5">
                Save
              </button>
            )}
            <Link to="/users-list" className="btn btn-primary text-uppercase px-5 mx-2">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}

export default BasicInformation;
